import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customerKeys } from '../customer.keys';
import { customerService } from '../customerService';
import type { OfferType } from '../types';
import {
  ASYNC_REFETCH_DELAYS_MS,
  refetchAfterAsyncChange,
} from './asyncRefetch';

export interface GenerateOfferVariables {
  customerId: string;
  loanId: string;
  offerType: OfferType;
}

export const GENERATE_OFFER_ERROR_CODES = {
  /** offerType not 0/1/2 or malformed body — the modal never sends this. */
  INVALID: 'INVALID_REQUEST',
  /** Loan missing, or it belongs to a different customer. */
  NOT_FOUND: 'LOAN_NOT_FOUND',
} as const;

export class GenerateOfferError extends Error {
  errorCode: string;
  details?: string;

  constructor(errorCode: string, message: string, details?: string) {
    super(message);
    this.name = 'GenerateOfferError';
    this.errorCode = errorCode;
    this.details = details;
  }
}

/**
 * The 400 `message` is a stringified Pydantic field-error list, not staff
 * prose — map to a friendly line instead of surfacing it.
 */
function parseApiError(error: unknown): GenerateOfferError {
  const err = (error ?? {}) as Record<string, unknown>;
  const errorCode = (err['code'] || err['errorCode']) as string | undefined;
  const friendly =
    errorCode === GENERATE_OFFER_ERROR_CODES.INVALID
      ? 'The offer tier was rejected. Pick Tier 0, 1 or 2 and try again.'
      : errorCode === GENERATE_OFFER_ERROR_CODES.NOT_FOUND
        ? 'This advance no longer exists.'
        : 'Failed to generate the offer. Please try again.';
  return new GenerateOfferError(
    errorCode ?? 'UNKNOWN',
    friendly,
    err['details'] as string | undefined
  );
}

/**
 * The generate-offer consumer is TWO FIFO hops (wipe → regenerate), so the
 * new offer can land later than a single-hop 202. Add a third pass after the
 * standard 3s/8s so the Offers tab catches up without a manual reload.
 */
export const GENERATE_OFFER_REFETCH_DELAYS_MS: readonly number[] = [
  ...ASYNC_REFETCH_DELAYS_MS,
  15000,
];

/**
 * POST /customers/{customerId}/loans/{loanId}/offers — wipe every offer on
 * the loan and generate + send a fresh one of the given tier (SCRUM-1547).
 * 202 Accepted: refetch the offers list, the loan (`hasOffer` flips to true
 * on a loan that never had one — that is what shows the Offers tab) and the
 * customer's loans list on the async delays.
 */
export function useGenerateOffer() {
  const queryClient = useQueryClient();

  return useMutation<undefined, GenerateOfferError, GenerateOfferVariables>({
    mutationFn: async ({ customerId, loanId, offerType }) => {
      try {
        await customerService.generateOffer(customerId, loanId, offerType);
      } catch (error) {
        throw parseApiError(error);
      }
      return undefined;
    },
    onSuccess: (_data, { customerId, loanId }) => {
      refetchAfterAsyncChange(
        queryClient,
        [
          customerKeys.offers(customerId, loanId),
          customerKeys.loan(customerId, loanId),
          customerKeys.loans(customerId),
        ],
        GENERATE_OFFER_REFETCH_DELAYS_MS
      );
    },
  });
}
